import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { bootstrapAuth } from './store/slices/authSlice';
import { BreadcrumbProvider } from './contexts/BreadcrumbContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Components
import Layout from './components/Layout/Layout/Layout';
import SessionTimeout from './components/Common/SessionTimeout/SessionTimeout';
import { subscribeToSessionExpired } from './services/sessionEvents.js';
import { getPracticeAdminHomePath } from './utils/roleUtils';
import LoginPage from './pages/Auth/LoginPage';
import OTPStep from './pages/Auth/OTPStep';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';

// Pages
import UserManagement from './pages/UserManagement/UserManagement';
import PracticeManagement from './pages/PracticeManagement/PracticeManagement';
import PracticeDashboard from './pages/PracticeManagement/PracticeDashboard/PracticeDashboard';
import AddPractice from './pages/PracticeManagement/PracticesList/AddPractice/AddPractice';
import AddProvider from './pages/PracticeManagement/ProviderManagement/AddProvider/AddProvider';
import AddCaregiver from './pages/PracticeManagement/CaregiverManagement/AddCaregiver/AddCaregiver';
import AddPatient from './pages/PracticeManagement/PatientManagement/AddPatient/AddPatient';
import PatientDetails from './pages/PracticeManagement/PatientManagement/PatientDetails';
import PatientEditView from './pages/PracticeManagement/PatientManagement/PatientEditView/PatientEditView';
import FollowUpList from './pages/PracticeManagement/PatientManagement/FollowUpList/FollowUpList';
import ImportPatients from './pages/PracticeManagement/ImportPatients/ImportPatients';
import AddAdmin from './pages/PracticeManagement/AdminManagement/AddAdmin/AddAdmin';
import AdminDashboard from './pages/AdminManagement/AdminDashboard';
import AdminDetails from './pages/PracticeManagement/AdminManagement/AdminDetails/AdminDetails';
import AddSystemCaregiver from './pages/SystemCaregiverDashboard/AddSystemCaregiver/AddSystemCaregiver';
import MeasurementData from './pages/MeasurementData/MeasurementData';
import QuestionnaireSystem from './pages/QuestionnaireSystem/QuestionnaireSystem';
import ChatSystem from './pages/ChatSystem/ChatSystem';
import SupportTicketsPage from './pages/SupportTickets/SupportTicketsPage';
import BillingDashboard from './pages/BillingReports/BillingDashboard';
import ManageBillingReport from './pages/BillingReports/ManageBillingReport';
import BillingCycleReport from './pages/BillingReports/BillingCycleReport';
import SentBillingReports from './pages/BillingReports/SentBillingReports';
import Settings from './pages/Settings/Settings';
import PermissionSettingsPage from './pages/Settings/PermissionSettingsPage';
import CarePrograms from './pages/CarePrograms/CarePrograms';
import AddCareProgram from './pages/CarePrograms/AddCareProgram/AddCareProgram';
import InventoryList from './pages/Inventory/InventoryList/InventoryList';
import InventoryDetails from './pages/Inventory/InventoryDetails/InventoryDetails';
import EventLogsPage from './pages/EventLogs/EventLogsPage';
import VisitType from './pages/CalendarSettings/VisitType/VisitType';
import WorkingHours from './pages/CalendarSettings/WorkingHours/WorkingHours';
import Calendar from './pages/CalendarSettings/Calendar/Calendar';
import FollowUpQuery from './pages/CareManagement/FollowUpQuery';
import CCMDashboard from './pages/CCM/CCMDashboard';
import CCMConfiguration from './pages/CCM/CCMConfiguration';
import EnrollmentQueue from './pages/CCM/EnrollmentQueue';
import EnrollPatient from './pages/CCM/EnrollPatient';
import EnrolledPatients from './pages/CCM/EnrolledPatients';
import CarePlans from './pages/CCM/CarePlans';
import Timer from './pages/CCM/Timer';
import TimeTracking from './pages/CCM/TimeTracking';
import Billing from './pages/CCM/Billing';
import PHMHub from './pages/CCM/PHMHub';

// Role-based Dashboard Pages
import ProviderDashboard from './pages/ProviderDashboard/ProviderDashboard';
import CaregiverDashboard from './pages/CaregiverDashboard/CaregiverDashboard';
import SystemCaregiverDashboard from './pages/SystemCaregiverDashboard/SystemCaregiverDashboard';
import SystemCaregiverDetails from './pages/SystemCaregiverDashboard/SystemCaregiverDetails/SystemCaregiverDetails';
import PracticeAdminHome from './pages/PracticeAdminDashboard/PracticeAdminHome';
import PracticeAdminLegacyRedirect from './pages/PracticeAdminDashboard/PracticeAdminLegacyRedirect';
import PatientDashboard from './pages/PatientDashboard/PatientDashboard';
import PatientSettings from './pages/PatientDashboard/PatientSettings/PatientSettings';
import DashboardSwitch from './components/Common/DashboardSwitch';
import ScrollToTop from './components/Common/ScrollToTop/ScrollToTop';

// Protected Route Component
import ProtectedRoute from './components/Auth/ProtectedRoute/ProtectedRoute';
import NonPatientRoute from './components/Auth/NonPatientRoute/NonPatientRoute';
import SuperAdminRoute from './components/Auth/SuperAdminRoute/SuperAdminRoute';
import BlockGlobalPracticesRoute from './components/Auth/PracticeAdminRoute/BlockGlobalPracticesRoute';
import PracticeScopeRoute from './components/Auth/PracticeAdminRoute/PracticeScopeRoute';
import RoleBasedRedirect from './components/Common/RoleBasedRedirect/RoleBasedRedirect';
import LoginWelcomeToast from './components/Auth/LoginWelcomeToast/LoginWelcomeToast';
import AppUpdateModal from './components/Common/AppUpdateModal/AppUpdateModal';
import AssignedReviewsModal from './components/AssignedReviewsModal/AssignedReviewsModal';
import AiAssistantWidget from './components/AiAssistant/AiAssistantWidget';
import PatientManagementRoutes from './pages/PatientManagement';
import socketService from './services/socketService';
import { setUpdateAvailable } from './store/slices/uiSlice';

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, isLoading, isBootstrapped, user } = useSelector((state) => state.auth);

  const getProtectedRedirectTarget = () => {
    const roleId = Number(user?.role_id);
    // Caregiver users must always land on their own dashboard.
    if (roleId === 5) return '/caregiver-dashboard';
    if (roleId === 7) return '/system-caregiver-dashboard';
    if (roleId === 6) return '/patient-dashboard';
    if (roleId === 4) return '/provider-dashboard';
    if (roleId === 3) return getPracticeAdminHomePath(user);

    if (typeof window === 'undefined') {
      return '/dashboard';
    }
    const storedPath = sessionStorage.getItem('lastVisitedPath');
    if (!storedPath || storedPath === '/login' || storedPath === '/') {
      return '/dashboard';
    }
    return storedPath;
  };

  const protectedRedirectTarget = getProtectedRedirectTarget();

  const hasInitialized = React.useRef(false);

  useEffect(() => {
    // Require tab-scoped session on load (cleared when tab/browser closes)
    if (!hasInitialized.current) {
      dispatch(bootstrapAuth());
      hasInitialized.current = true;
    }
  }, [dispatch]);

  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Catch-all for session expiration
    const unsubscribe = subscribeToSessionExpired(() => {
      handleSmoothRedirect();
    });

    return () => unsubscribe();
  }, []);

  // Global Socket.IO app-update listener (login + authenticated screens).
  // Registered once at app root; cleaned up with socket.off on unmount.
  useEffect(() => {
    socketService.connect();
    socketService.onAppUpdate(() => {
      dispatch(setUpdateAvailable(true));
    });

    return () => {
      socketService.offAppUpdate();
    };
  }, [dispatch]);

  const handleSmoothRedirect = () => {
    if (isRedirecting) return;
    
    setIsRedirecting(true);

    try {
      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const timeoutUrl = `${apiBase.replace(/\/$/, '')}/users/log-session-timeout`;
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        navigator.sendBeacon(timeoutUrl);
      } else {
        fetch(timeoutUrl, { method: 'POST', credentials: 'include', keepalive: true }).catch(() => {});
      }
    } catch (_) {
      // Best-effort: still send the user to login if the beacon fails.
    }
    
    // Explicitly clear auth state to stop the "Navigate back to dashboard" loop
    dispatch({ type: 'auth/clearAuth' }); 
    navigate('/login', { state: { sessionExpired: true }, replace: true });
    
    // Short delay to let navigation settle before allowing next redirect
    setTimeout(() => {
      setIsRedirecting(false);
    }, 1000);
  };



  return (
    <BreadcrumbProvider>
      <div className="App">
        <ScrollToTop />
        {/* Peaceful Redirect Overlay */}

        <SessionTimeout />
        <LoginWelcomeToast />
        <AppUpdateModal />
        {isAuthenticated ? <AssignedReviewsModal /> : null}
        {isAuthenticated ? <AiAssistantWidget /> : null}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          style={{
            top: '90px',
            right: '20px',
            zIndex: 260000
          }}
          toastStyle={{
            fontSize: '14px',
            padding: '12px 16px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 260000
          }}
        />
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
            }
          />
          <Route
            path="/verify-otp"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <OTPStep />
            }
          />
          <Route
            path="/forgot-password"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <ForgotPasswordPage />
            }
          />
          <Route
            path="/reset-password"
            element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <ResetPasswordPage />
            }
          />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardSwitch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/:practiceId"
            element={
              <ProtectedRoute>
                <NonPatientRoute>
                  <Layout>
                    <PracticeDashboard />
                  </Layout>
                </NonPatientRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <Layout>
                  <UserManagement />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <SuperAdminRoute>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </SuperAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/add"
            element={
              <ProtectedRoute>
                <SuperAdminRoute>
                  <Layout>
                    <AddAdmin />
                  </Layout>
                </SuperAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/:adminId"
            element={
              <ProtectedRoute>
                <SuperAdminRoute>
                  <Layout>
                    <AdminDetails />
                  </Layout>
                </SuperAdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices"
            element={
              <ProtectedRoute>
                <BlockGlobalPracticesRoute>
                  <NonPatientRoute>
                    <Layout>
                      <PracticeManagement />
                    </Layout>
                  </NonPatientRoute>
                </BlockGlobalPracticesRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/add"
            element={
              <ProtectedRoute>
                <BlockGlobalPracticesRoute>
                  <NonPatientRoute>
                    <Layout>
                      <AddPractice />
                    </Layout>
                  </NonPatientRoute>
                </BlockGlobalPracticesRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/:practiceId"
            element={
              <ProtectedRoute>
                <PracticeScopeRoute>
                  <NonPatientRoute>
                    <Layout>
                      <PracticeDashboard />
                    </Layout>
                  </NonPatientRoute>
                </PracticeScopeRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/:practiceId/add-provider"
            element={
              <ProtectedRoute>
                <PracticeScopeRoute>
                  <NonPatientRoute>
                    <Layout>
                      <AddProvider />
                    </Layout>
                  </NonPatientRoute>
                </PracticeScopeRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/:practiceId/add-caregiver"
            element={
              <ProtectedRoute>
                <PracticeScopeRoute>
                  <NonPatientRoute>
                    <Layout>
                      <AddCaregiver />
                    </Layout>
                  </NonPatientRoute>
                </PracticeScopeRoute>
              </ProtectedRoute>
            }
          />
          {/* Add patient OR edit-in-place from list (?editId= patients.id) — single long form (AddPatient.jsx) */}
          <Route
            path="/practices/:practiceId/add-patient"
            element={
              <ProtectedRoute>
                <PracticeScopeRoute>
                  <NonPatientRoute>
                    <Layout>
                      <AddPatient />
                    </Layout>
                  </NonPatientRoute>
                </PracticeScopeRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/:practiceId/patients/:patientId"
            element={
              <ProtectedRoute>
                <PracticeScopeRoute>
                  <NonPatientRoute>
                    <Layout>
                      <PatientDetails />
                    </Layout>
                  </NonPatientRoute>
                </PracticeScopeRoute>
              </ProtectedRoute>
            }
          />
          {/* Tabbed patient workspace (profile / schedule / meters / docs) — PatientEditView.jsx; not the list ?editId= form */}
          <Route
            path="/practices/:practiceId/patients/:patientId/edit"
            element={
              <ProtectedRoute>
                <PracticeScopeRoute>
                  <NonPatientRoute>
                    <Layout>
                      <PatientEditView />
                    </Layout>
                  </NonPatientRoute>
                </PracticeScopeRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/:practiceId/patients/:patientId/follow-ups"
            element={
              <ProtectedRoute>
                <PracticeScopeRoute>
                  <NonPatientRoute>
                    <Layout>
                      <FollowUpList />
                    </Layout>
                  </NonPatientRoute>
                </PracticeScopeRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/:practiceId/patients/:patientId/measurements/*"
            element={
              <ProtectedRoute>
                <PracticeScopeRoute>
                  <NonPatientRoute>
                    <Layout>
                      <MeasurementData />
                    </Layout>
                  </NonPatientRoute>
                </PracticeScopeRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/:practiceId/import-patients"
            element={
              <ProtectedRoute>
                <PracticeScopeRoute>
                  <NonPatientRoute>
                    <Layout>
                      <ImportPatients />
                    </Layout>
                  </NonPatientRoute>
                </PracticeScopeRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practices/:practiceId/add-admin"
            element={
              <ProtectedRoute>
                <PracticeScopeRoute>
                  <NonPatientRoute>
                    <Layout>
                      <AddAdmin />
                    </Layout>
                  </NonPatientRoute>
                </PracticeScopeRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-caregiver-dashboard/add-system-caregiver"
            element={
              <ProtectedRoute>
                <Layout>
                  <AddSystemCaregiver />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-caregiver-dashboard/:caregiverId"
            element={
              <ProtectedRoute>
                <Layout>
                  <SystemCaregiverDetails />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Role-based Dashboard Routes */}
          <Route
            path="/provider-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProviderDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-caregiver-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <SystemCaregiverDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practice-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <PracticeAdminHome />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practice-admin-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <PracticeAdminHome />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/practice-admin-dashboard/:practiceId"
            element={
              <ProtectedRoute>
                <PracticeAdminLegacyRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/caregiver-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <CaregiverDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patient-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <PatientDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Legacy patient login redirect URLs */}
          <Route
            path="/patient-details"
            element={
              <ProtectedRoute>
                <Navigate to="/patient-dashboard" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/patient-details/:practiceId/:userId"
            element={
              <ProtectedRoute>
                <Navigate to="/patient-dashboard" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/patient-settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <PatientSettings />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/patients/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <PatientManagementRoutes />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/measurements"
            element={
              <ProtectedRoute>
                <Layout>
                  <MeasurementData />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/questionnaires"
            element={
              <ProtectedRoute>
                <Layout>
                  <QuestionnaireSystem />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <Layout>
                  <ChatSystem />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat-access"
            element={<Navigate to="/settings?tab=chat-access" replace />}
          />
          <Route
            path="/support-tickets"
            element={
              <ProtectedRoute>
                <Layout>
                  <SupportTicketsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/sent-reports"
            element={
              <ProtectedRoute>
                <Layout>
                  <SentBillingReports />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <Layout>
                  <BillingDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/manage/:practiceId"
            element={
              <ProtectedRoute>
                <Layout>
                  <ManageBillingReport />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing/cycle/:practiceId"
            element={
              <ProtectedRoute>
                <Layout>
                  <BillingCycleReport />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Layout>
                  <BillingDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/care-programs"
            element={
              <ProtectedRoute>
                <Layout>
                  <CarePrograms />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/care-programs/add"
            element={
              <ProtectedRoute>
                <Layout>
                  <AddCareProgram />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/care-programs/edit/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <AddCareProgram />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory"
            element={
              <ProtectedRoute>
                <Layout>
                  <InventoryList />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory/:productId"
            element={
              <ProtectedRoute>
                <Layout>
                  <InventoryDetails />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/email-logs"
            element={<Navigate to="/settings?tab=email-logs" replace />}
          />
          <Route
            path="/event-logs/:moduleId/:targetUserId"
            element={
              <ProtectedRoute>
                <Layout>
                  <EventLogsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/events-log"
            element={<Navigate to="/settings?tab=user-logs" replace />}
          />

          <Route
            path="/mobile-app-access"
            element={<Navigate to="/settings?tab=mobile-app-access" replace />}
          />

          <Route
            path="/permission/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <PermissionSettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar-setting/visit-type"
            element={
              <ProtectedRoute>
                <Layout>
                  <VisitType />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar-setting/working-hours"
            element={
              <ProtectedRoute>
                <Layout>
                  <WorkingHours />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar-setting/calendar"
            element={
              <ProtectedRoute>
                <Layout>
                  <Calendar />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/care/follow-up"
            element={
              <ProtectedRoute>
                <Layout>
                  <FollowUpQuery />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* CCM Routes */}
          <Route
            path="/ccm/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <CCMDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/configuration"
            element={
              <ProtectedRoute>
                <Layout>
                  <CCMConfiguration />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/enrollment/queue"
            element={
              <ProtectedRoute>
                <Layout>
                  <EnrollmentQueue />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/enroll/:patientId"
            element={
              <ProtectedRoute>
                <Layout>
                  <EnrollPatient />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/enrolled"
            element={
              <ProtectedRoute>
                <Layout>
                  <EnrolledPatients />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/care-plans/:enrollmentId"
            element={
              <ProtectedRoute>
                <Layout>
                  <CarePlans />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/phm-hub/:enrollmentId"
            element={
              <ProtectedRoute>
                <Layout>
                  <PHMHub />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/timer"
            element={
              <ProtectedRoute>
                <Layout>
                  <Timer />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/time-tracking"
            element={
              <ProtectedRoute>
                <Layout>
                  <TimeTracking />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/ccm/billing"
            element={
              <ProtectedRoute>
                <Layout>
                  <Billing />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Root redirect */}
          <Route
            path="/"
            element={<Navigate to={isAuthenticated ? protectedRedirectTarget : "/login"} replace />}
          />

          {/* Catch all route */}
          <Route
            path="*"
            element={<Navigate to={isAuthenticated ? protectedRedirectTarget : "/login"} replace />}
          />
        </Routes>
      </div>
    </BreadcrumbProvider>
  );
}

export default App;
